import brand from '@helpers/brand';
import { fonts } from '@helpers/fonts';
import { ButtonStyles } from '@stories/Components/Buttons/Button/Button.styles';
import SelectStyles from '@stories/Components/Forms/Select/Select.styles';
import { TextAreaStyles } from '@stories/Components/Forms/TextArea/TextArea.styles';
import TextboxStyles from '@stories/Components/Forms/Textbox/Textbox.styles';
import { Containers } from '@stories/Widgets/YourSolution/YS/Components/Shared/Step.styles';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';
import Check from '@img/icons/tick.svg';
import CheckboxStyles from '@stories/Components/Forms/Checkbox/Checkbox.styles';
import { Device, from, until } from '@helpers/media';
import { fluid } from '@helpers/fluid';
import { ThemeSwitch } from '@helpers/theme';
const CheckboxStyleOverride = css `
  font-size: 18px !important;
  line-height: 22px !important;
  ${fonts.DaxPro.SlightlyLessThanMedium};
  color: ${brand.grey.grey20} !important;
  cursor: pointer;
  ${ThemeSwitch({ groundforceColour: brand.primary.alt })}

  &::before {
    border-color: var(--theme);
  }

  &::after {
    padding: 0;
    width: 21px;
    height: 21px;
    border: none;
    background-color: var(--theme);
    background-image: url(${Check});
    background-size: 13px 16px;
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 1px;
  }
`;
const Wrapper = styled.div `
  position: relative;
  ${ThemeSwitch({ groundforceColour: brand.primary.alt })}

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: min(
      500px,
      calc(100% - ${(props) => props['data-contact-section-height']}px - ${fluid(35, 75)})
    );
    clip-path: polygon(0 0, 100% 90%, 100% 100%, 0% 100%);
    background-color: var(--theme);
    z-index: -1;
  }
`;
const Container = styled(Containers.Wide) `
  padding-top: ${fluid(15, 52)};
  gap: 31px;

  @media ${until(Device.TabletLarge)} {
    display: flex;
    flex-direction: column;
  }

  @media ${from(Device.TabletLarge)} {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  @media ${until(Device.DesktopLarge)} {
    grid-column: 1 / -1;
  }
`;
const FormWrapper = styled.div `
  padding: 16px;
  background-color: ${brand.white};
  display: flex;
  flex-direction: column;
  gap: 30px;
`;
const FormLayout = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  grid-template-rows: max-content;
`;
const FormField = styled.div `
  grid-column: span 2;

  @media ${from(Device.TabletLarge)} {
    &[data-half-width='true'] {
      grid-column: span 1;
    }
  }

  &[data-is-checkbox='true'] {
    display: flex;
    align-items: flex-start;

    ${CheckboxStyles.Label} {
      margin-bottom: 0px !important;
      padding-right: 28px;
      padding-left: 0;
      width: 100%;

      &::after,
      &::before {
        left: unset;
        right: 0;
      }
    }
  }

  *::placeholder {
    font-size: 18px;
    line-height: 22px;
    color: #57575780;
    ${fonts.DaxPro.SlightlyLessThanMedium};
  }

  label {
    font-size: 18px !important;
    line-height: 22px !important;
    ${fonts.DaxPro.Medium};
    color: ${brand.grey.grey35} !important;
    margin-bottom: 8px !important;
  }

  ${CheckboxStyles.Label} {
    ${fonts.DaxPro.SlightlyLessThanMedium};
    color: ${brand.grey.grey20} !important;

    ${CheckboxStyleOverride}
  }

  ${TextAreaStyles.Input} {
    height: 150px;
  }

  ${SelectStyles.Element} {
    min-width: unset;
  }

  ${TextboxStyles.Input}, ${TextAreaStyles.Input}, ${SelectStyles.Wrapper}, ${SelectStyles.Element} {
    width: 100%;
  }
`;
const FormProgressWrapper = styled.div `
  grid-column: span 2;
  display: grid;
  grid-template-columns: repeat(${(props) => props['data-page-count']}, 1fr);
  gap: 15px;
`;
const FormProgressTab = styled(m.div).attrs((props) => ({
    initial: 'hidden',
    animate: props['data-active'] ? 'visible' : 'hidden',
})) `
  grid-column: span 1;
  border-radius: 2px;
  height: 6px;
  background-color: ${brand.grey.grey89};
  position: relative;
`;
const FormProgressTabActive = styled(m.span).attrs(() => ({
    variants: {
        hidden: {
            width: '0%',
        },
        visible: {
            width: '100%',
        },
    },
    transition: {
        bounce: 0,
        ease: 'easeInOut',
        duration: 0.75,
    },
})) `
  position: absolute;
  background-color: var(--theme);
  height: 100%;
  border-radius: 2px;
`;
const FormPageTitle = styled.h3 `
  grid-column: span 2;
  font-size: ${fluid(23, 25)};
  line-height: 24px;
  ${fonts.DaxPro.Bold};
  margin: 0;
`;
const ButtonWrapper = styled.div `
  grid-column: span 2;
  display: grid;
  gap: ${fluid(15, 30)};

  @media ${until(Device.MobileLarge)} {
    display: flex;
    flex-direction: column;
  }

  @media ${from(Device.MobileLarge)} {
    grid-template-columns: 1fr 1fr;
  }

  ${ButtonStyles.Button}:only-child {
    grid-column: 1 / span 2;
  }
`;
const ChecklistWrapper = styled.div `
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const Checklist = styled.div `
  @media ${until(Device.TabletLarge)} {
    display: flex;
    flex-direction: column;
  }
  @media ${from(Device.TabletLarge)} {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  gap: ${fluid(10, 16)};

  label {
    ${CheckboxStyleOverride}
  }
`;
const SmallText = styled.small `
  ${fonts.DaxPro.SlightlyLessThanMedium};
  color: ${brand.grey.grey76} !important;
  font-size: ${fluid(14, 16)};
  line-height: ${fluid(16, 20)};
  white-space: pre-line;
`;
const FileUpload = styled.div `
  display: flex;
  flex-direction: column;
  gap: 5px;

  input {
    display: inline-block;
    border-radius: 3px;
    border: 1px solid rgba(140, 140, 140, 0.75);
    ${fonts.DaxPro.Regular};
    font-size: 18px;
    color: ${brand.black};
    letter-spacing: -0.02em;
    line-height: 1em;
    margin: 0;
    opacity: 1;
    padding: 18px;
    text-transform: none;
    transition: opacity 0.3s ease-out;
    width: auto;
  }
`;
const CheckboxWrapper = styled.div `
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;

  ${() => SmallText} {
    margin-top: 16px;
    color: ${brand.black} !important;
    line-height: 24px;

    a {
      color: var(--theme);
      ${fonts.DaxPro.SlightlyLessThanMedium};
      text-decoration: underline;
    }
  }
`;
const ErrorLabel = styled.div `
  font-size: 16px;
  line-height: 20px;
  ${fonts.DaxPro.Medium};
  color: ${brand.red.base};
  margin-top: -25px;
  grid-column: span 2;
`;
const ErrorHeading = styled.h4 `
  text-decoration: underline;
  font-size: 16px;
  line-height: 20px;
  ${fonts.DaxPro.Medium};
  color: ${brand.red.base};
`;
const Message = styled.span `
  font-size: 18px;
  line-height: 22px;
  ${fonts.DaxPro.SlightlyLessThanMedium};
  color: var(--theme);
`;
export const MultiStepFormStyles = {
    Wrapper,
    Container,
    FormLayout,
    FormField,
    FormProgressWrapper,
    FormProgressTab,
    FormProgressTabActive,
    FormPageTitle,
    ButtonWrapper,
    FormWrapper,
    ChecklistWrapper,
    Checklist,
    FileUpload,
    SmallText,
    CheckboxWrapper,
    ErrorHeading,
    ErrorLabel,
    Message,
};
